import { useState } from "react";
import { Download, Upload, Loader2, Brain } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

// Moves the cached expert analyses between environments: preview and production
// have separate databases, so a fresh deploy starts with an empty cache.
export const AiCacheCard = () => {
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [overwrite, setOverwrite] = useState(false);

  const exportCache = async () => {
    setBusy("export");
    try {
      const data = await adminApi.exportAiCache();
      const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `foinn-ai-cache-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.count} analyses`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setBusy("");
    }
  };

  const importCache = async (file) => {
    if (!file) return;
    setBusy("import");
    setResult(null);
    try {
      const parsed = JSON.parse(await file.text());
      const analyses = Array.isArray(parsed) ? parsed : parsed.analyses || [];
      const res = await adminApi.importAiCache(analyses, overwrite);
      setResult(res);
      toast.success(`${res.inserted} added · ${res.total} in cache`);
    } catch (e) {
      toast.error("Import failed — is it a Foinn.org cache file?");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded-2xl border border-[#e5e0d8] bg-white p-4" data-testid="ai-cache-card">
      <h3 className="flex items-center gap-2 font-serif text-xl font-bold text-[#2d2824]">
        <Brain className="h-5 w-5 text-[#8a2be2]" /> Expert analysis cache
      </h3>
      <p className="mt-1 text-xs font-semibold text-[#6e655b]">
        Preview and production use separate databases. Export here, then import the file from the
        admin panel of the other environment — no AI credits are spent.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          onClick={exportCache}
          disabled={!!busy}
          className="flex items-center gap-2 rounded-full bg-[#2d2824] px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
          data-testid="ai-cache-export"
        >
          {busy === "export" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export .json
        </button>

        <label
          className="flex cursor-pointer items-center gap-2 rounded-full border-2 border-[#2d2824] px-4 py-2 text-sm font-bold text-[#2d2824]"
          data-testid="ai-cache-import-label"
        >
          {busy === "import" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          Import .json
          <input
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => importCache(e.target.files?.[0])}
            data-testid="ai-cache-import"
          />
        </label>

        <label className="flex items-center gap-1.5 text-xs font-bold text-[#6e655b]">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
            data-testid="ai-cache-overwrite"
          />
          overwrite existing
        </label>
      </div>

      {result && (
        <p className="mt-2 text-xs font-bold text-[#0f8a5f]" data-testid="ai-cache-result">
          {result.inserted} added · {result.updated} updated · {result.skipped} skipped ·{" "}
          {result.total} total in cache
        </p>
      )}
    </div>
  );
};
